import * as z from "zod";
import { CardInfo, CardSummary, ScpData, SearchLinks } from "./card";
import { isoDate } from "./common";
import { ITEM_KINDS, type ItemKind, ItemSummary, SealedInfo } from "./item";
import { MARKET_REFRESH_OUTCOMES, MarketEstimate } from "./market";

/** A reviewer's corrections, for whichever kind of item the scan is. */
export type ScanCorrections = Partial<CardInfo> | Partial<SealedInfo>;

export const SCAN_REVIEWS = ["PENDING", "CONFIRMED", "REJECTED"] as const;
export type ScanReview = (typeof SCAN_REVIEWS)[number];

export const PRICING_STATUSES = ["priced", "no_match", "degraded"] as const;
export const MATCH_KINDS = ["CODE", "PRODUCT", "IDENTITY", "CREATED", "NONE", "LINKED"] as const;

/** An eBay listing of a candidate: evidence only, never a price. */
export const CandidateListing = z.object({
  image: z.string(),
  url: z.string().nullable(),
  title: z.string().nullable(),
});
export type CandidateListing = z.output<typeof CandidateListing>;

/** One product the pricing lookup weighed for a scan, as the pipeline streams it. */
export const ProductCandidate = z.object({
  /** The product as the pricing source returned it; its `id` is what a pick names. */
  product: ScpData,
  /** 0 is the pick. */
  rank: z.number(),
  /** 0–100. */
  confidence: z.number().nullable(),
  /** Listings eBay's visual search tagged with this product. */
  listingVotes: z.number(),
  /** Photos of listings of this product. */
  images: z.array(z.string()),
  /** The listings those photos belong to, same order. */
  listings: z.array(CandidateListing).default([]),
});
export type ProductCandidate = z.output<typeof ProductCandidate>;

/** A stored candidate: the streamed one plus whether the shop stocks it. */
export const ScanCandidate = ProductCandidate.extend({
  matchedCardId: z.string().nullable(),
  matchKind: z.enum(["PRODUCT", "NONE"]),
});
export type ScanCandidate = z.output<typeof ScanCandidate>;

export const CODE_ORIGINS = ["DEVICE", "CUSTOMER", "AI"] as const;
export type CodeOrigin = (typeof CODE_ORIGINS)[number];

/** A code a reviewer may approve as naming the item; only an approval makes it a vote. */
export const SuggestedCode = z.object({
  type: z.enum(["upc", "ean", "cert"]),
  value: z.string(),
  origin: z.enum(CODE_ORIGINS),
});
export type SuggestedCode = z.output<typeof SuggestedCode>;

export const CatalogProductSummary = z.object({
  id: z.string(),
  title: z.string(),
  imageUrl: z.string().nullable(),
});
export type CatalogProductSummary = z.output<typeof CatalogProductSummary>;

/** What the shared catalogue says about a scan's codes. */
export const ScanCatalog = z.object({
  status: z.enum(["HIT", "CONFLICT", "MISS"]),
  /** On a HIT, the product the codes name; on a CONFLICT, the one a reviewer picked. */
  product: CatalogProductSummary.nullable(),
  /** On a CONFLICT, the products shops are split between, each with its votes. */
  candidates: z.array(CatalogProductSummary.extend({ votes: z.number() })).default([]),
});
export type ScanCatalog = z.output<typeof ScanCatalog>;

export const CODE_OUTCOMES = ["unknown", "conflict", "unavailable"] as const;
export type CodeOutcome = (typeof CODE_OUTCOMES)[number];

export const Scan = z.object({
  recognitionOutcome: z.enum(["recognized", "no_match", "needs_photo"]).nullable().optional(),
  id: z.string(),
  storeId: z.string(),
  locationId: z.string().nullable(),
  kind: z.enum(["OWNER", "CUSTOMER"]),
  customerId: z.string().nullable(),
  imageUrl: z.string(),
  /** False for a scan started from a barcode alone; `imageUrl` is then the product's image or "". */
  hasPhoto: z.boolean().default(true),
  backImageUrl: z.string().nullable(),
  /** What kind of item was scanned. Null until the pipeline decides. */
  itemKind: z.enum(ITEM_KINDS).nullish(),
  /** Why a barcode-only scan's codes named no item; the scan then needs its photo. */
  codeOutcome: z.enum(CODE_OUTCOMES).nullable().default(null),
  /** The reading in force: the reviewer's edits over the model's. */
  cardInfo: CardInfo.nullable(),
  /** The same, for sealed product. */
  sealedInfo: SealedInfo.nullish(),
  /** The model's own output, never rewritten. */
  modelCardInfo: CardInfo.nullable(),
  /** The same, for sealed product. */
  modelSealedInfo: SealedInfo.nullish(),
  /** Sealed fields edited since the candidates were searched; empty when current. */
  searchStaleBecause: z.array(z.string()).default([]),
  /** The reviewer's asking price; null while it follows the estimate. */
  draftPriceCents: z.number().nullish(),
  scpData: ScpData.nullable(),
  /** Every product the lookup weighed, the pick first. Empty when it weighed none or predates the list. */
  candidates: z.array(ScanCandidate).default([]),
  searchLinks: SearchLinks.nullable(),
  pricingStatus: z.enum(PRICING_STATUSES).nullable(),
  scanProgress: z.string().nullable(),
  scanProgressAt: isoDate.nullable(),
  review: z.enum(SCAN_REVIEWS),
  matchedCardId: z.string().nullable(),
  matchKind: z.enum(MATCH_KINDS),
  /** Null when the scan carried no codes. */
  catalog: ScanCatalog.nullable().default(null),
  suggestedCodes: z.array(SuggestedCode).default([]),
  /** Null until confirmed. */
  cardId: z.string().nullable(),
  card: CardSummary.nullable(),
  /** What it resolved to, as any kind. */
  item: ItemSummary.nullish(),
  estimatedPriceCents: z.number().nullable(),
  estimatedPriceFrom: z.string().nullable(),
  estimatedPriceGrade: z.string().nullable(),
  /** Sealed: the shared market estimate of the product it resolved to. */
  marketEstimate: MarketEstimate.nullish(),
  reviewedAt: isoDate.nullable(),
  heldCopy: z.boolean().nullable(),
  /** The customer quote's standing; expired once past its deadline. */
  quoteState: z.enum(["active", "expired", "sold", "withdrawn"]).nullable(),
  quotePriceCents: z.number().nullable(),
  quoteExpiresAt: isoDate.nullable(),
  timestamp: isoDate,
  createdAt: isoDate,
  updatedAt: isoDate,
});
export type Scan = z.output<typeof Scan>;

/** A code read on the device: a retail barcode, or a grader's cert number off a slab. */
export interface ScanCode {
  type: "upc" | "ean" | "cert";
  value: string;
}

/**
 * The images to scan: hosted URLs or `data:` URLs, as the SPA sends them. A
 * sealed product may start from its UPC/EAN in `codes` with no image; the
 * photo, if it is needed, follows under the same `attemptId`.
 */
export interface ScanStart {
  enableNoMatch?: boolean;
  attemptId?: string;
  imageUrl?: string;
  backImageUrl?: string;
  /** Codes decoded on the device. One the catalogue knows skips identification. */
  codes?: ScanCode[];
  /** The kind of item, when the caller already knows. Omit to let the scanner decide. */
  kind?: ItemKind;
  /** Staff only: scan for a counter (POS) customer the shop created. */
  customerId?: string;
  /** Staff only: scan this Shopify product from its own photos; confirming lists the card under it. */
  shopifyProductId?: string;
}

export interface ScanListQuery {
  enableNoMatch?: boolean;
  review?: ScanReview;
  locationId?: string;
  customerId?: string;
  updatedAfter?: Date;
}

export const ScanEdit = z.object({
  scan: Scan,
  /** Whether the edit forced a fresh pricing lookup. */
  repriced: z.boolean(),
  repricedBecause: z.array(z.string()),
  /** The lookup ran and the pricing source failed; a sealed scan keeps its product. */
  searchFailed: z.boolean().default(false),
});
export type ScanEdit = z.output<typeof ScanEdit>;

/** How an edit is saved. */
export interface ScanEditOptions {
  /** false saves without a pricing lookup, true runs one; omitted, the edit decides. */
  search?: boolean;
  /** The reviewer's asking price; null hands it back to the estimate. */
  priceCents?: number | null;
}

/** What to set on the card a confirmation creates or matches. */
export interface ScanConfirmOptions {
  corrections?: ScanCorrections;
  /** Below this the shop does not sell. */
  floorPriceCents?: number;
  /** The asking price. */
  priceCents?: number;
  stock?: { quantity?: number; locationId?: string | null; costCents?: number | null; certNumber?: string | null };
  /** Approves what the codes name, as the shop's vote in the shared catalogue. Left out, no vote. */
  catalogApproval?: CatalogApproval;
  /** A hand-made sealed item (`sealed.list` with `filter: "linkable"`) to confirm onto. One scan only. */
  linkItemId?: string;
  /** With `linkItemId`: the linked item takes the scan's name. */
  adoptScanName?: boolean;
}

/** `seenCatalogProductId` is `catalog.product.id` when the catalogue said HIT, else null. */
export interface CatalogApproval {
  codes: ScanCode[];
  seenCatalogProductId: string | null;
}

type CatalogScan = Pick<Scan, "suggestedCodes" | "catalog">;

/** The product the shared catalogue named when the reviewer looked, as `CatalogApproval` wants it. */
export const seenCatalogProductId = (scan: Pick<Scan, "catalog">): string | null =>
  scan.catalog?.status === "HIT" ? (scan.catalog.product?.id ?? null) : null;

export const codeKey = (code: Pick<ScanCode, "type" | "value">) => `${code.type}:${code.value}`;

/** Approved to start with: what the shop's own device read, unless shops disagree about it. */
export function defaultApprovedCodes(scan: CatalogScan): string[] {
  if (scan.catalog?.status === "CONFLICT") return [];
  return scan.suggestedCodes.filter((code) => code.origin === "DEVICE").map(codeKey);
}

/** The approval a confirm sends for the codes approved by `codeKey`; none casts no vote. */
export function catalogApproval(scan: CatalogScan, approved: readonly string[]): CatalogApproval | undefined {
  const codes = scan.suggestedCodes
    .filter((code) => approved.includes(codeKey(code)))
    .map(({ type, value }) => ({ type, value }));
  return codes.length > 0 ? { codes, seenCatalogProductId: seenCatalogProductId(scan) } : undefined;
}

export const ScanConfirmation = z.object({
  cardId: z.string(),
  stockId: z.string().nullable(),
  offerId: z.string().nullable(),
});
export type ScanConfirmation = z.output<typeof ScanConfirmation>;

export const REVIEW_BUCKETS = ["new", "exists", "unvetted"] as const;

export const CardFieldDifference = z.object({
  field: z.string(),
  scan: z.string(),
  card: z.string(),
});
export type CardFieldDifference = z.output<typeof CardFieldDifference>;

/**
 * Pending scans that resolve to one identity. `new`: confirming creates a card.
 * `exists`: a card a person approved. `unvetted`: a card a customer's scan
 * created that nobody has looked at yet.
 */
export const ScanReviewGroup = z.object({
  key: z.string(),
  bucket: z.enum(REVIEW_BUCKETS),
  scanIds: z.array(z.string()),
  count: z.number(),
  sample: Scan,
  card: CardSummary.nullable(),
  /** The proposed item, as any kind. */
  item: ItemSummary.nullish(),
  /** Where the reading and the proposed card disagree. Empty means they agree. */
  differences: z.array(CardFieldDifference),
});
export type ScanReviewGroup = z.output<typeof ScanReviewGroup>;

export const SCAN_STATES_IN_SHOP = [
  "pending",
  "approved",
  "in-cart",
  "paid",
  "rejected",
  "expired",
  "unrecognized",
  "unavailable",
] as const;

/** A customer's own scan in a shop, with the state the buyer sees. */
export const MyScan = Scan.extend({
  stateInShop: z.enum(SCAN_STATES_IN_SHOP),
  priceCents: z.number().nullable(),
  expiresAt: isoDate.nullable(),
  offerId: z.string().nullable(),
  /** The cart that decided `stateInShop`: the PAID one if any, else the OPEN one. Scans sharing it were bought together. */
  customerCartId: z.string().nullish(),
  /** When that cart was paid; null unless `stateInShop` is `paid`. */
  paidAt: isoDate.nullish(),
});
export type MyScan = z.output<typeof MyScan>;

/** Whether a scan is sealed product: by its kind, or by its reading while the kind is not yet stored. */
export function isSealedScan(scan: Pick<Scan, "itemKind" | "sealedInfo"> | null | undefined): boolean {
  if (!scan) return false;
  return scan.itemKind ? scan.itemKind === "sealed" : scan.sealedInfo != null;
}

/** A sealed scan's product name as far as it is known; null for a card, which each app titles its own way. */
export function sealedScanName(scan: Pick<Scan, "itemKind" | "sealedInfo" | "item">): string | null {
  if (!isSealedScan(scan)) return null;
  return scan.item?.title || scan.sealedInfo?.product_full_name.trim() || null;
}

/** The picture to show for a scan: its photo, else the item's own for a scan started from a barcode alone. */
export function scanImageUrl(scan: Pick<Scan, "imageUrl" | "hasPhoto" | "item" | "card">): string | null {
  if (scan.hasPhoto && scan.imageUrl) return scan.imageUrl;
  return scan.item?.imageUrl || scan.card?.imageUrl || scan.imageUrl || null;
}

// ─── The scan pipeline's live stream ─────────────────────────────────────────

export const SCAN_STAGES = ["preparation", "identification", "pricing", "persistence"] as const;
export type ScanStage = (typeof SCAN_STAGES)[number];

/**
 * One frame of `startStream`.
 *
 * A discriminated union on `type`, reassembled from the SSE frame: the backend
 * writes the name on an `event:` line and the payload on a `data:` line, and the
 * JSON carries no `type` of its own.
 *
 * `scan.error` is a **frame, not a rejection**. Once the stream's headers are
 * out there is no status left to fail with, so a pipeline failure arrives here
 * like any other event.
 */
export const ScanStreamEvent = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("scan.started"),
    /**
     * `scanId`: the scan the stream reports on, sent when the start names an attempt or asks for no-match scans,
     * so a stream cut short can be settled by reading it.
     */
    data: z.object({ stage: z.enum(SCAN_STAGES), scanId: z.string().optional() }),
  }),
  z.object({
    type: z.literal("scan.prepared"),
    data: z.object({ imageUrl: z.string(), backImageUrl: z.string().nullable() }),
  }),
  z.object({
    type: z.literal("scan.identified"),
    data: z.object({ cardInfo: z.record(z.string(), z.unknown()) }),
  }),
  z.object({
    type: z.literal("scan.priced"),
    data: z.object({
      scpData: ScpData.nullable(),
      candidates: z.array(ProductCandidate).default([]),
      searchLinks: SearchLinks,
      searchOnEbay: z.string(),
      /** The pricing stage runs on every scan, so the event alone says nothing about whether a price was found. */
      pricingDegraded: z.boolean(),
      pricingStatus: z.enum(["priced", "no_match", "degraded"]),
    }),
  }),
  z.object({ type: z.literal("scan.completed"), data: Scan }),
  z.object({
    type: z.literal("scan.error"),
    data: z.object({ error: z.string(), stage: z.enum(SCAN_STAGES), terminal: z.boolean().optional() }),
  }),
]);
export type ScanStreamEvent = z.output<typeof ScanStreamEvent>;

export function newScanAttemptId(): string {
  if (typeof crypto?.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** A pending sealed scan after a market refresh: its estimate, and what the refresh did. */
export const ScanMarketRefresh = z.object({
  scan: Scan,
  outcome: z.enum(MARKET_REFRESH_OUTCOMES),
});
export type ScanMarketRefresh = z.output<typeof ScanMarketRefresh>;
